// UserContext.js
// Manage user context across the app
import React, { createContext, useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom"; // If you need navigation after login
import { fetchUserLeagues } from "./utils/fetchUserLeagues"; // Import your utility function
import { isTokenExpired, clearCookie } from "./utils/tokenManagement";  // Import token management utilities

const UserContext = createContext();

// Provider to wrap the app and provide user context and initialize user and leagues data
export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [leagues, setLeagues] = useState([]);
  const navigate = useNavigate(); // Hook to navigate after logout

  // Load and intialize user and leagues from localStorage when the app first loads
  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    const storedLeagues = localStorage.getItem("leagues");

    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }

    if (storedLeagues) {    
      setLeagues(JSON.parse(storedLeagues));
    }
  }, []); // Empty deps to run once on mount

  // Fetch leagues when the user is available
  useEffect(() => {
    const fetchLeagues = async () => {
      if (user && user.token) {
        // Check if the token is expired before attempting to fetch leagues
        if (isTokenExpired(user.token)) {
          clearCookie('authToken');  // Clear token from cookies
          navigate("/");  // Redirect to login page
        } else {
          try {
            const leaguesData = await fetchUserLeagues(user._id, user.token, navigate); // Pass navigate to handle redirection in fetchUserLeagues
            setLeagues(leaguesData); // Update state with fetched leagues
            localStorage.setItem("leagues", JSON.stringify(leaguesData)); // Persist leagues in localStorage
          } catch (error) {
            console.error("Error fetching leagues:", error);
          }
        }
      }
    };
  
    fetchLeagues();
  }, [user]); // Run whenever `user` changes

  // Persist user and leagues in localStorage whenever they change
  useEffect(() => {
    if (user) {
      localStorage.setItem("user", JSON.stringify(user)); // Store the user object
    }
  }, [user]); // Only run this when user changes

  // Persist leagues in localStorage whenever they change
  useEffect(() => {
    if (leagues.length > 0) {
      localStorage.setItem("leagues", JSON.stringify(leagues)); // Store the leagues array
    }
  }, [leagues]); // Only run this when leagues change

  const logout = () => {
    // Clear user data from local storage
    localStorage.removeItem('user');
    localStorage.removeItem('leagues');
    localStorage.removeItem('leagueId');
    setUser(null);
    setLeagues([]);
    navigate('/');
  };
  

  return (
    <UserContext.Provider value={{ user, setUser, leagues, setLeagues, logout }}>
      {children}
    </UserContext.Provider>
  );
};

// Hook to access user context
export const useUser = () => {
  return useContext(UserContext);
};
